import React from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PrayerForm from "@/components/PrayerForm";
import SEOHead from "@/components/SEOHead";

const Home = () => {
  return (
    <>
      <SEOHead
        title="Ieshia Danielle, Minister & Stewardship Coach: From Burnout to Becoming"
        description="Take back control of your life and become the woman you were created to be. Scripture-rooted coaching, workshops, and tools to move from burnout to becoming."
        canonical="https://iamieshiadanielle.com/"
        ogImage="https://vibe.filesafe.space/1787853701390843161/assets/a5d05b54-0df1-474c-a727-ad33093a5f8e.png"
        schemaJson={[
          {
            "@type": "WebPage",
            "@id": "https://iamieshiadanielle.com/#webpage",
            url: "https://iamieshiadanielle.com/",
            name: "Ieshia Danielle, Minister & Stewardship Coach: From Burnout to Becoming",
            isPartOf: { "@id": "https://iamieshiadanielle.com/#website" },
            about: { "@id": "https://iamieshiadanielle.com/#person" },
            inLanguage: "en-US",
          },
          {
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://iamieshiadanielle.com",
              },
            ],
          },
          {
            "@type": "VideoObject",
            name: "From Burnout to Becoming with Ieshia Danielle",
            description:
              "Ieshia Danielle introduces the journey from burnout to becoming: uncovering what has been quietly costing you and stewarding the life God has given you.",
            contentUrl:
              "https://assets.cdn.filesafe.space/N5B75kjUT85NEZBh2RaZ/media/6a66326bfddfeb1aa96f1e3a.mp4",
            thumbnailUrl:
              "https://assets.cdn.filesafe.space/N5B75kjUT85NEZBh2RaZ/media/6a946184a90361de8b517bcb.jpg",
            uploadDate: "2026-01-01",
          },
        ]}
      />
      <div className="min-h-screen bg-[#FAF6F0] text-[#380C1E] flex flex-col font-sans">
        <Header />

        <main className="flex-1">
          {/* Video Hero Section */}
          <section className="relative w-full h-[60vh] md:h-[80vh] min-h-[420px] overflow-hidden bg-[#380C1E]">
            <video
              className="absolute inset-0 w-full h-full object-cover"
              src="https://assets.cdn.filesafe.space/N5B75kjUT85NEZBh2RaZ/media/6a66326bfddfeb1aa96f1e3a.mp4"
              autoPlay
              loop
              muted
              playsInline
              preload="none"
              poster="https://assets.cdn.filesafe.space/N5B75kjUT85NEZBh2RaZ/media/6a946184a90361de8b517bcb.jpg"
              onError={(e) => {
                (e.currentTarget as HTMLVideoElement).style.display = "none";
              }}
            >
              <track kind="captions" />
            </video>
            <div className="absolute inset-0 bg-gradient-to-b from-[#380C1E]/40 via-[#380C1E]/30 to-[#FAF6F0]" />
            <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
              <p className="text-[11px] md:text-xs uppercase tracking-[0.3em] font-semibold text-white/90 mb-4">
                Minister · Stewardship Coach
              </p>
              <p className="font-serif text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-normal leading-[1.1] text-white max-w-4xl drop-shadow-lg">
                From burnout to{" "}
                <span className="italic font-light text-[#F5E9D7]">
                  becoming.
                </span>
              </p>
              <p className="mt-5 text-sm md:text-lg text-white/90 max-w-xl font-light leading-relaxed">
                Take back control of your life and become the woman you were
                created to be.
              </p>
              <div className="mt-8 flex flex-wrap gap-4 justify-center">
                <a
                  href="https://quiz.iamieshiadanielle.com/quiz"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#9E1B50] hover:bg-[#801340] text-white font-bold uppercase tracking-wider text-[11px] px-8 py-4 rounded-full transition-all shadow-lg"
                >
                  Take the 6 B's Quiz →
                </a>
                <a
                  href="https://members.iamieshiadanielle.com/communities/groups/the-becoming-community-f/home?invite=6ac3b9839636d4d8a70e9471"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white/10 backdrop-blur-sm border border-white/60 hover:bg-white hover:text-[#4A0E26] text-white font-bold uppercase tracking-wider text-[11px] px-8 py-4 rounded-full transition-all"
                >
                  Join My free community →
                </a>
              </div>
            </div>
          </section>

          {/* Hero Section */}
          <section className="bg-white py-12 md:py-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              {/* Left Text */}
              <div className="lg:col-span-7 space-y-6">
                <p className="text-xs uppercase tracking-widest font-semibold text-[#380C1E]/70">
                  Minister · Stewardship Coach
                </p>

                <h1 className="font-serif sm:text-5xl lg:text-6xl font-normal leading-[1.15] text-[#380C1E] text-2xl">
                  Take back control of your life and{" "}
                  <span className="italic font-serif font-light text-[#9E1B50]">
                    become
                  </span>{" "}
                  the woman you were created to be.
                </h1>

                <p className="sm:text-lg text-[#380C1E]/80 max-w-xl leading-relaxed font-light text-sm">
                  Let’s uncover what has been quietly costing you, break the
                  cycles keeping you in survival mode, and steward the life God
                  has given you.
                </p>

                <div className="pt-2 flex flex-wrap gap-4 items-center">
                  <a
                    href="https://quiz.iamieshiadanielle.com/quiz"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-[#4A0E26] hover:bg-[#330A1F] text-white font-bold uppercase tracking-wider text-[11px] px-8 py-4 rounded-full transition-all shadow-sm"
                  >
                    Take the 6 B's Quiz →
                  </a>
                  <Link
                    to="/programs/becoming-intensive"
                    className="bg-transparent border border-[#4A0E26] hover:bg-[#4A0E26] hover:text-white text-[#4A0E26] font-bold uppercase tracking-wider text-[11px] px-8 py-4 rounded-full transition-all"
                  >
                    Join the Intensive →
                  </Link>
                </div>
              </div>

              {/* Right Image */}
              <div className="lg:col-span-5">
                <div className="rounded-[2.5rem] overflow-hidden bg-neutral-200 shadow-lg border border-[#E8DEC8] aspect-square">
                  <img
                    src="https://assets.cdn.filesafe.space/N5B75kjUT85NEZBh2RaZ/media/6a946184a90361de8b517bcb.jpg"
                    alt="Ieshia Danielle holding her Bible"
                    width={800}
                    height={800}
                    fetchPriority="high"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Workbook Showcase Section */}
          <section className="bg-white py-16 md:py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-5">
                <div className="rounded-[2.5rem] overflow-hidden bg-neutral-200 shadow-md border border-[#E8DEC8] aspect-square">
                  <img
                    src="https://assets.cdn.filesafe.space/N5B75kjUT85NEZBh2RaZ/media/6a946455934a62aec895514d.jpg"
                    alt="Ieshia Danielle working at her desk"
                    width={800}
                    height={800}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              </div>

              <div className="lg:col-span-7 space-y-6">
                <p className="text-xs uppercase tracking-widest font-semibold text-[#380C1E]/70">
                  A guided purpose workbook
                </p>

                <h2 className="font-serif text-3xl md:text-5xl font-normal text-[#380C1E]">
                  From burnout to{" "}
                  <span className="italic font-serif text-[#9E1B50]">
                    becoming.
                  </span>
                </h2>

                <p className="text-lg md:text-xl font-serif text-[#380C1E]/90 font-normal">
                  Five sections dedicated to making sure you become who you were
                  designed to be.
                </p>

                <p className="text-[#380C1E]/80 leading-relaxed font-light text-base max-w-xl">
                  Created for the mom who feels stretched thin. You will uncover
                  which of the six burnout sources is draining you, reclaim your
                  identity, and plan your becoming one page at a time.
                </p>

                <div className="bg-[#FAF0E6] rounded-2xl p-4 flex items-center gap-6 max-w-md border border-[#E8DEC8]">
                  <span className="text-2xl font-serif font-bold text-[#380C1E]">
                    $27
                  </span>
                  <span className="text-xs uppercase font-medium text-[#380C1E]/70 tracking-wider">
                    Self-paced · practical · Scripture-rooted
                  </span>
                </div>

                <div className="pt-2 flex flex-wrap gap-4">
                  <a
                    href="https://store.iamieshiadanielle.com/product-details/product/from-burnout-to-becoming-purpose-workbook-for-moms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-[#9E1B50] hover:bg-[#801340] text-white font-bold uppercase text-[11px] tracking-wider px-8 py-4 rounded-full inline-block transition-all shadow-sm"
                  >
                    PHYSICAL WORKBOOK →
                  </a>
                  <a
                    href="https://store.iamieshiadanielle.com/product-details/product/6a86712d71e03b860a5c7a77"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-[#4A0E26] hover:bg-[#330A1F] text-white font-bold uppercase text-[11px] tracking-wider px-8 py-4 rounded-full inline-block transition-all shadow-sm"
                  >
                    Digital Workbook →
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* The Becoming Pathway Section */}
          <section className="bg-[#FBF1F4] py-16 md:py-24 border-y border-[#E8DEC8]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-16">
              <p className="text-xs uppercase font-semibold tracking-widest text-[#9E1B50] italic mb-2">
                The Becoming Pathway
              </p>
              <h2 className="font-serif text-3xl md:text-5xl font-normal text-[#380C1E] mb-4">
                Start where you are.
              </h2>
              <p className="text-[#380C1E]/80 max-w-2xl mx-auto text-base font-light leading-relaxed">
                Burnout did not happen in a day, and becoming will not either.
                There are three ways to walk it, and each one leads into the
                next.
              </p>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Offer 01 */}
              <div className="bg-white rounded-3xl p-8 flex flex-col justify-between border border-[#E8DEC8] shadow-sm hover:shadow-md transition-shadow relative">
                <div className="space-y-4">
                  <div className="flex items-center justify-between border-b border-[#E8DEC8] pb-4">
                    <span className="text-xs font-bold text-[#380C1E]">01</span>
                    <span className="text-[10px] uppercase tracking-widest font-semibold text-[#380C1E]">
                      Begin on your own
                    </span>
                  </div>
                  <h3 className="font-serif text-2xl font-normal text-[#380C1E]">
                    The Workbook
                  </h3>
                  <p className="text-[#380C1E]/90 text-sm leading-relaxed font-light">
                    Name what is draining you and create a written plan for what
                    comes next.
                  </p>
                </div>
                <div className="pt-8">
                  <a
                    href="https://store.iamieshiadanielle.com/product-details/product/from-burnout-to-becoming-purpose-workbook-for-moms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#380C1E] font-bold uppercase text-[11px] tracking-wider hover:text-[#9E1B50] transition-colors"
                  >
                    Start for $27 →
                  </a>
                </div>
              </div>

              {/* Offer 02 */}
              <div className="bg-[#9E1B50] text-white rounded-3xl p-8 flex flex-col justify-between shadow-md relative">
                <div className="space-y-4">
                  <div className="flex items-center justify-between border-b border-white/30 pb-4">
                    <span className="text-xs font-bold text-white">02</span>
                    <span className="text-[10px] uppercase tracking-widest font-semibold text-white">
                      Renew your thinking
                    </span>
                  </div>
                  <h3 className="font-serif text-2xl font-normal text-white">
                    Identity Reset
                  </h3>
                  <p className="text-white text-sm leading-relaxed font-light">
                    Challenge the beliefs and labels that have been quietly
                    shaping your choices.
                  </p>
                </div>
                <div className="pt-8">
                  <Link
                    to="/programs/identity-reset"
                    className="text-white font-bold uppercase text-[11px] tracking-wider hover:underline"
                  >
                    Reset for $47 →
                  </Link>
                </div>
              </div>

              {/* Offer 03 */}
              <div className="bg-[#4A0E26] text-white rounded-3xl p-8 flex flex-col justify-between shadow-md relative">
                <div className="space-y-4">
                  <div className="flex items-center justify-between border-b border-white/30 pb-4">
                    <span className="text-xs font-bold text-white">03</span>
                    <span className="text-[10px] uppercase tracking-widest font-semibold text-white">
                      Transform with support
                    </span>
                  </div>
                  <h3 className="font-serif text-2xl font-normal text-white">
                    Becoming Intensive
                  </h3>
                  <p className="text-white text-sm leading-relaxed font-light">
                    Six weeks of teaching, coaching, structure, and
                    accountability across the six B's.
                  </p>
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-serif text-xl font-semibold text-white">
                      $997
                    </span>
                  </div>
                </div>
                <div className="pt-8">
                  <Link
                    to="/programs/becoming-intensive"
                    className="text-white font-bold uppercase text-[11px] tracking-wider hover:underline"
                  >
                    Join for $997 →
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* Book Ieshia Section */}
          <section className="bg-[#4A0E26] text-white py-16 md:py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-6 space-y-6">
                <p className="text-xs uppercase tracking-widest font-semibold text-white/70">
                  Book Ieshia
                </p>
                <h2 className="font-serif text-3xl md:text-5xl font-normal text-white">
                  Bring the message into the room.
                </h2>
                <p className="text-white/80 text-base md:text-lg leading-relaxed font-light">
                  Honest, Scripture-rooted teaching on burnout, stewardship,
                  identity, emotional wholeness, and becoming who God made you
                  to be.
                </p>
                <div>
                  <Link
                    to="/book"
                    className="bg-white hover:bg-[#FAF6F0] text-[#4A0E26] font-bold uppercase text-[11px] tracking-wider px-8 py-4 rounded-full inline-block transition-all shadow-sm"
                  >
                    Invite Ieshia to speak →
                  </Link>
                </div>
              </div>

              <div className="lg:col-span-6">
                <div className="rounded-[2.5rem] overflow-hidden bg-neutral-900 border border-white/10 shadow-lg">
                  <img
                    src="https://assets.cdn.filesafe.space/N5B75kjUT85NEZBh2RaZ/media/6a9464a90914f112151fba9c.jpg"
                    alt="Ieshia Danielle connecting with women at a live event"
                    width={1200}
                    height={420}
                    loading="lazy"
                    className="w-full h-[420px] object-cover object-center"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Between Me & You Podcast Showcase */}
          <section className="bg-white py-16 md:py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-5">
                <div className="rounded-[2.5rem] overflow-hidden bg-neutral-200 border border-[#E8DEC8] shadow-md aspect-[4/6]">
                  <img
                    src="https://assets.cdn.filesafe.space/N5B75kjUT85NEZBh2RaZ/media/6a95dfe29ced56098ad84f43.jpg"
                    alt="Ieshia Danielle recording in the Between Me & You podcast studio"
                    width={800}
                    height={1200}
                    loading="lazy"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              </div>

              <div className="lg:col-span-7 space-y-6">
                <p className="text-xs uppercase tracking-widest font-semibold text-[#380C1E]/70">
                  Between Me & You Podcast
                </p>
                <h2 className="font-serif text-3xl md:text-5xl font-normal text-[#380C1E]">
                  The room where the truth gets told.
                </h2>
                <p className="text-[#380C1E]/80 text-base md:text-lg leading-relaxed font-light">
                  Open and honest conversations about emotions, unspoken trauma,
                  identity, faith, and the God who meets us in the middle of it
                  all.
                </p>
                <div>
                  <Link
                    to="/podcast"
                    className="bg-[#4A0E26] hover:bg-[#330A1F] text-white font-bold uppercase text-[11px] tracking-wider px-8 py-4 rounded-full inline-block transition-all shadow-sm"
                  >
                    Watch or listen →
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* Prayer Form */}
          <PrayerForm />
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Home;
